import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowUpCircle } from '../components/icons';
import { Button } from '../components/Button';
import { t } from '../i18n';
import { colors, radius, screenPadding, spacing, typography } from '../theme/tokens';
import { ouvrirLien } from '../utils/ouvrirLien';

interface UpdateRequiredScreenProps {
  storeUrl: string;
}

/**
 * Écran bloquant — la version installée est antérieure au minimum exigé (`app_versions`).
 *
 * IL DIT POURQUOI, PAS SEULEMENT QUOI. C'est la seule raison pour laquelle il existe sous cette
 * forme : une application qui refuse de s'ouvrir sans expliquer passe pour cassée, et se fait
 * désinstaller plutôt que mettre à jour. L'ordre du texte suit celui des questions — qu'est-ce qui
 * se passe, est-ce que j'ai perdu quelque chose, qu'est-ce que je fais.
 *
 * AUCUNE SESSION, AUCUNE DONNÉE PERSONNELLE, AUCUNE NAVIGATION. Il peut apparaître avant la
 * connexion : il ne lit aucun store, n'émet aucune requête et ne reçoit que l'URL du store. Y
 * afficher un prénom supposerait une session qui n'existe pas forcément à ce moment-là.
 *
 * PAS D'ÉCHAPPATOIRE : ni « plus tard », ni fermeture, ni retour. Un blocage contournable laisserait
 * croire que la version reste utilisable.
 */
export function UpdateRequiredScreen({ storeUrl }: UpdateRequiredScreenProps) {
  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']} testID="update-required-screen">
      <View style={styles.contenu}>
        <View style={styles.pastille}>
          <ArrowUpCircle size={32} color={colors.bluePrimary} />
        </View>

        <Text style={styles.titre}>{t('update.blocking.title')}</Text>
        <Text style={styles.corps}>{t('update.blocking.body')}</Text>
        <Text style={styles.rassurance}>{t('update.blocking.reassurance')}</Text>

        <View style={styles.action}>
          <Button
            title={t('update.blocking.action')}
            onPress={() => ouvrirLien(storeUrl)}
            withArrow
            testID="update-required-action"
          />
        </View>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  contenu: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: screenPadding,
    gap: spacing.md,
  },
  pastille: {
    width: 64,
    height: 64,
    borderRadius: radius.xl,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.blueSoft,
    marginBottom: spacing.sm,
  },
  titre: { fontSize: typography.h1.size, fontWeight: typography.h1.weight, lineHeight: typography.h1.lineHeight, color: colors.grey900 },
  corps: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
  rassurance: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey500 },
  action: { marginTop: spacing.lg },
});
